<template>
  <div
    id="diagnostics"
    class="pa-4"
  >
    <subtitle>
      {{ $t("diagnostics.compare_players.label") }}
    </subtitle>

    <v-row class="mt-2">
      <v-col>
        <player-selection-card
          :player="player1"
          :loading="loadingPlayer1"
          @selectPlayer="playersDialog=true; selectPlayer1 = true"
        />
      </v-col>
      <v-col>
        <player-selection-card
          :player="player2"
          :loading="loadingPlayer2"
          @selectPlayer="playersDialog=true; selectPlayer2 = true"
        />
      </v-col>
    </v-row>

    <div v-if="player1.id || player2.id">
      <v-row>
        <v-col>
          <v-card
            v-if="stats.player1.loading || stats.player2.loading"
            class="d-flex justify-center fill-height align-center rounded-lg"
            height="467"
          >
            <v-progress-circular
              indeterminate
              size="50"
            />
          </v-card>
          <v-card
            v-else
            class="rounded-lg"
          >
            <v-card-title>
              <v-row align="center">
                <v-col>
                  <div class="subtitle-2 grey--text text--grey-100">
                    {{ $t('card_title.diagnostics_trend') }}
                  </div>
                </v-col>
                <v-col cols="auto">
                  <v-select
                    v-model="diagnosticProtocols.selected"
                    solo
                    return-object
                    :items="diagnosticProtocolsGrouped"
                    dense
                    item-value="name"
                    item-text="name"
                    :label="$t('dropdown.select_protocol.title')"
                    hide-details
                    style="width: 250px"
                  />
                </v-col>
                <v-col
                  class="ml-2"
                  cols="auto"
                >
                  <v-select
                    v-model="variablesGrouped.selected"
                    solo
                    return-object
                    :items="variablesGrouped.data"
                    dense
                    item-value="var_id"
                    item-text="var_name"
                    :label="$t('dropdown.select_variables_to_show.title')"
                    hide-details
                    style="width: 250px"
                  />
                </v-col>
                <v-col
                  cols="auto"
                  class="ml-2"
                >
                  <v-btn-toggle
                    v-model="dateRange"
                    color="primary"
                    mandatory
                  >
                    <v-btn
                      small
                      :value="1"
                    >
                      {{ $t('button.month_one') }}
                    </v-btn>
                    <v-btn
                      small
                      :value="3"
                    >
                      {{ $t('button.month_three') }}
                    </v-btn>
                    <v-btn
                      small
                      :value="6"
                    >
                      {{ $t('button.month_six') }}
                    </v-btn>
                    <v-btn
                      small
                      :value="12"
                    >
                      {{ $t('button.year_one') }}
                    </v-btn>
                    <v-btn
                      small
                      :value="60"
                    >
                      {{ $t('button.year_five') }}
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
                <v-col
                  cols="auto"
                  class="ml-2"
                >
                  <v-btn-toggle
                    v-model="view"
                    color="primary"
                    mandatory
                  >
                    <v-btn
                      small
                      class="text-none"
                      value="visual"
                      :disabled="trendStats.length === 1"
                    >
                      {{ $t('card.display_option.visual') }}
                    </v-btn>
                    <v-btn
                      small
                      class="text-none"
                      value="list"
                    >
                      {{ $t('card.display_option.list') }}
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>
            </v-card-title>
            <line-chart
              v-if="view === 'visual'"
              :raw-data="trendStats"
              class="elevation-0"
              x-value="start_at"
              :colors="['#54A1F0', '#C354FF']"
              :chart-value-keys="[{ key: 'player1.value', label: player1 ? player1.name : 'Player 1' }, ...(player2.id ? [{ key: 'player2.value', label: player2 ? player2.name : 'Player 2' }] : []) ]"
              :markers="{size: 4}"
            />
            <v-data-table
              v-else
              :headers="[{value: 'date', text: $t('picker.date.title'), width: '15%'}, {value: 'name', text: $t('player.label.title'), width: '25%'}, {value: 'value', text: $t('diagnostics_group.list_header.value'), width: '17.5%'}, {value: 'value', text: $t('diagnostics_group.list_header.value'), width: '17.5%'}, {value: 'name', text: $t('player.label.title'), width: '25%'}]"
              :items="trendStats"
              :loading="stats.loading"
              height="397"
              fixed-header
              :items-per-page="50"
              hide-default-footer
              dense
              disable-sort
            >
              <template v-slot:item="{ item }">
                <tr>
                  <td>
                    {{ $dayjs(item.start_at).format('DD.MM.YYYY.') }}
                  </td>
                  <td>
                    {{ getPlayerName('player1') }}
                  </td>
                  <td :style="{color: getValeForDate(item.start_at, 'player1') > getValeForDate(item.start_at, 'player2') ? 'green' : 'red'}">
                    {{ getValeForDate(item.start_at, 'player1') }}
                  </td>
                  <td :style="{color: getValeForDate(item.start_at, 'player1') < getValeForDate(item.start_at, 'player2') ? 'green' : 'red'}">
                    {{ getValeForDate(item.start_at, 'player2') }}
                  </td>
                  <td>
                    {{ getPlayerName('player2') }}
                  </td>
                </tr>
              </template>
              <template v-slot:no-data>
                <div class="text-grey text-caption align-self-center">
                  {{ $t('card.diagnostics_trend.select_protocol.hint') }}
                </div>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-spacer />
        <v-col cols="auto">
          <v-btn
            text
            class="text-none"
            color="primary"
            @click="filtersModal = true"
          >
            {{ $t("button.filters") }}
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <div
      v-if="loadingPlayer1 || loadingPlayer2"
      class="d-flex justify-center align-center mt-16"
    >
      <v-progress-circular
        indeterminate
        size="50"
      />
    </div>

    <div
      v-else-if="player1.diagnosticStats || player2.diagnosticStats"
      class="mt-4"
    >
      <v-row>
        <v-col>
          <line-chart
            :raw-data="groupStats"
            :chart-value-keys="[ { key: composite === 'composite' ? 'player1.comp_value' : 'player1.norm_comp_value', label: player1 ? player1.name : 'Player 1', type: 'bar' },...(player2.id ? [{ key: composite === 'composite' ? 'player2.comp_value' : 'player2.norm_comp_value', label: player2 ? player2.name : 'Player 2', type: 'bar' }] : []) ]"
            x-value="group_id"
            :xaxis="{
              labels: {
                rotate: -45,
                hideOverlappingLabels: false,
                trim: true
              },
              categories: groupStats.map(group => group.name)
            }"
            :yaxis="[composite === 'composite' ? {min: -3, max: 3} : {min: 0, max: 100}]"
            :fill="{ type: 'solid', opacity: [0.5] }"
            :colors="['#54A1F0', '#C354FF']"
          >
            <template v-slot:header>
              <v-row align="center">
                <v-col>
                  <v-card-title class="px-6">
                    <div class="subtitle-2 grey--text text--grey-100">
                      {{ $t('card_title.composite_graph') }}
                    </div>
                  </v-card-title>
                </v-col>
                <v-col
                  class="mr-2"
                  cols="auto"
                >
                  <v-btn-toggle
                    v-model="composite"
                    mandatory
                    class="mr-6"
                    color="primary"
                  >
                    <v-btn
                      small
                      class="text-none"
                      value="composite"
                    >
                      {{ $t('card.composite_graph.display_option.composite') }}
                    </v-btn>
                    <v-btn
                      small
                      class="text-none"
                      value="normalized"
                    >
                      {{ $t('card.composite_graph.display_option.normalized') }}
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>
            </template>
          </line-chart>
        </v-col>
      </v-row>

      <v-row>
        <v-col
          v-for="capability in capabilities"
          :key="capability.capability_id"
          cols="6"
        >
          <diagnostic-capability-card
            :capability="capability"
            :variables="variables"
            :player1="player1"
            :player2="player2"
          />
        </v-col>
      </v-row>
    </div>

    <card-dialog
      v-model="playersDialog"
      :title="$t('select_player.dialog.header')"
      max-width="600px"
      prevent-close
      @close="playersDialog = false; selectPlayer1 = false; selectPlayer2 = false"
    >
      <v-card-text>
        <v-row class="pt-4">
          <v-col>
            <v-text-field
              v-model="searchPlayer"
              dense
              outlined
              hide-details
              append-icon="fal fa-search"
              :label="$t('text_input.search_players.title')"
            />
          </v-col>
          <v-col cols="5">
            <v-select
              v-model="selectedTeam"
              :items="teams"
              return-object
              item-text="name"
              hide-details
              dense
              outlined
              clearable
              :label="$t('dropdown.select_team.title')"
              @input="fetchAllPlayers"
            />
          </v-col>
        </v-row>

        <div
          v-if="filteredPlayers.length === 0"
          class="text-center py-10"
        >
          {{ $t("playerList.noPlayers") }}
        </div>

        <v-list
          v-else
          class="v-list--bordered"
        >
          <v-list-item
            v-for="player in filteredPlayers"
            :key="player.id"
            :disabled="player.id === player1.id || player.id === player2.id"
            @click="selectPlayer1 ? player1 = player : player2 = player; playersDialog = false; selectPlayer1 = false; selectPlayer2 = false"
          >
            <v-list-item-avatar>
              <v-avatar
                size="32"
                class="elevation-2"
              >
                <v-img :src="player.photo_url">
                  <template v-slot:placeholder>
                    <v-icon x-small>
                      fal fa-user
                    </v-icon>
                  </template>
                </v-img>
              </v-avatar>
            </v-list-item-avatar>
            <v-list-item-content>
              <v-list-item-title>
                {{ player.name }}
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-icon>
                fal fa-arrow-right
              </v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-card-text>
    </card-dialog>

    <diagnostic-filters-dialog
      max-width="600px"
      :visible.sync="filtersModal"
      :filters.sync="filters"
      :player="player1"
      @click:apply="applyFilters"
    />
  </div>
</template>

<script>
import Subtitle from '@/components/helpers/Subtitle.vue'
import CardDialog from '@/components/helpers/CardDialog.vue'
import PlayerSelectionCard from '@/components/diagnostics/PlayerSelectionCard.vue'
import { DiagnosticProtocolApiService, PlayerApiService } from '@/data/services'
import LineChart from '@/components/charts/LineChart.vue'
import DiagnosticCapabilityCard from '@/components/diagnostics/DiagnosticCapabilityCard.vue'
import dayjs from 'dayjs'
import { uniqBy } from 'lodash'
import DiagnosticFiltersDialog from '@/components/diagnostics/DiagnosticFiltersDialog.vue'

export default {
  name: 'DiagnosticComparisonView',
  components: {
    DiagnosticFiltersDialog,
    DiagnosticCapabilityCard,
    LineChart,
    PlayerSelectionCard,
    CardDialog,
    Subtitle
  },
  data() {
    return {
      players: {
        data: [],
        loading: false
      },
      player1: {},
      player2: {},
      selectPlayer1: false,
      selectPlayer2: false,
      playersDialog: false,
      loadingPlayer1: false,
      loadingPlayer2: false,
      composite: 'composite',
      view: 'visual',
      dateRange: 3,
      diagnosticProtocols: {
        player1: {
          data: [],
          loading: false
        },
        player2: {
          data: [],
          loading: false
        },
        selected: null
      },
      stats: {
        player1: {
          data: [],
          loading: false
        },
        player2: {
          data: [],
          loading: false
        }
      },
      variablesGrouped: {
        data: [],
        selected: null
      },
      filters: {
        teamId: null,
        date: [],
        age: [1, 100],
        sex: null,
        sportId: null,
        countryCode: null,
        rankId: null,
        positionId: null,
        eventId: null
      },
      filtersModal: false,
      searchPlayer: null,
      selectedTeam: null
    }
  },
  computed: {
    teams() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    filteredPlayers() {
      return this.players.data.filter(player => {
        const search = this.searchPlayer ? this.searchPlayer.toLowerCase() : ''
        return player.name.toLowerCase().includes(search)
      })
    },
    groupStats() {
      const player1Groups = this.player1?.diagnosticStats?.groups || []
      const player2Groups = this.player2?.diagnosticStats?.groups || []

      const allGroups = [...new Set([
        ...player1Groups.map(g => g.group_id),
        ...player2Groups.map(g => g.group_id)
      ])]

      return allGroups.map(groupId => ({
        group_id: groupId,
        name: this.$t(`diagnostics.groups.${groupId}`),
        player1: player1Groups.find(g => g.group_id === groupId) || null,
        player2: player2Groups.find(g => g.group_id === groupId) || null
      }))
    },
    capabilities() {
      const player1Capabilities = this.player1?.diagnosticStats?.capabilities || []
      const player2Capabilities = this.player2?.diagnosticStats?.capabilities || []

      const onlyOnePlayerSelected = Boolean((this.player1.diagnosticStats && !this.player2.diagnosticStats) || (!this.player1.diagnosticStats && this.player2.diagnosticStats))

      const allCapabilities = [...new Set([
        ...player1Capabilities.map(c => c.capability_id),
        ...player2Capabilities.map(c => c.capability_id)
      ])]

      const player1CapabilityIds = player1Capabilities.map(c => c.capability_id)
      const player2CapabilityIds = player2Capabilities.map(c => c.capability_id)

      const capabilitiesToUse = onlyOnePlayerSelected
          ? allCapabilities
          : player1CapabilityIds.filter(capabilityId => player2CapabilityIds.includes(capabilityId))

      return capabilitiesToUse.map(capabilityId => ({
        capability_id: capabilityId,
        name: capabilityId,
        player1: player1Capabilities.find(c => c.capability_id === capabilityId) || null,
        player2: player2Capabilities.find(c => c.capability_id === capabilityId) || null
      }))
    },
    variables() {
      const player1Variables = this.player1?.diagnosticStats?.variables || []
      const player2Variables = this.player2?.diagnosticStats?.variables || []

      const variablesToUse = player2Variables.length === 0
          ? player1Variables.map(variable => variable.var_id)
          : player1Variables
              .filter(p1Var => player2Variables.some(p2Var => p2Var.var_id === p1Var.var_id))
              .map(variable => variable.var_id)

      return variablesToUse.map(variableId => {
        const player1Var = player1Variables.find(v => v.var_id === variableId) || null
        const player2Var = player2Variables.find(v => v.var_id === variableId) || null

        return {
          var_id: variableId,
          var_name: player1Var?.var_name || player2Var?.var_name || null,
          capability_id: player1Var?.capability_id || null,
          player1: player1Var,
          player2: player2Var
        }
      })
    },
    diagnosticProtocolsGrouped() {
      if (!this.player1.id && this.player2.id) {
        return this.diagnosticProtocols.player2.data || []
      }

      if (this.player1.id && !this.player2.id) {
        return this.diagnosticProtocols.player1.data || []
      }

      if (this.player1.id && this.player2.id) {
        if (!this.diagnosticProtocols.player1.data.length || !this.diagnosticProtocols.player2.data.length) {
          return []
        }

        const player1ProtocolIds = this.diagnosticProtocols.player1.data.map(p => p.id)
        const player2ProtocolIds = this.diagnosticProtocols.player2.data.map(p => p.id)

        const commonProtocolIds = player1ProtocolIds.filter(id => player2ProtocolIds.includes(id))

        return commonProtocolIds.map(id => {
          return this.diagnosticProtocols.player1.data.find(p => p.id === id)
        })
      }
      return []
    },
    trendStats() {
      if (!this.variablesGrouped.selected) return []

      const player1Stats = this.stats.player1.data
      const player2Stats = this.stats.player2.data

      const allStats = [...new Set([
        ...player1Stats.map(s => s.event.start_at),
        ...player2Stats.map(s => s.event.start_at)
      ])]

      return allStats.map(date => {
        const player1Stat = player1Stats.find(s => s.event.start_at === date) || null
        const player2Stat = player2Stats.find(s => s.event.start_at === date) || null

        const player1Value = player1Stat ? player1Stat.variables.find(v => v.var_id === this.variablesGrouped.selected.var_id)?.value : null
        const player2Value = player2Stat ? player2Stat.variables.find(v => v.var_id === this.variablesGrouped.selected.var_id)?.value : null

        return {
          start_at: date,
          player1: {
            value: player1Value
          },
          player2: {
            value: player2Value
          }
        }
      }).filter(p => p.player1.value || p.player2.value)
    }
  },
  watch: {
    player1: {
      handler(value, oldValue) {
        if (value && value.id && value.id !== oldValue.id) {
          this.fetchDiagnosticStats('player1', value)
          this.fetchDiagnosticProtocols('player1', value.id)
        }
      },
      deep: true
    },
    player2: {
      handler(value, oldValue) {
        if (value && value.id && value.id !== oldValue.id) {
          this.fetchDiagnosticStats('player2', value)
          this.fetchDiagnosticProtocols('player2', value.id)
        }
      },
      deep: true
    },
    diagnosticProtocolsGrouped() {
      if (this.diagnosticProtocolsGrouped.length) {
        this.diagnosticProtocols.selected = this.diagnosticProtocolsGrouped[0]
      } else {
        this.diagnosticProtocols.selected = null
        this.variablesGrouped.data = []
        this.stats.player1.data = []
        this.stats.player2.data = []
      }
    },
    'diagnosticProtocols.selected': function () {
      if (this.player1.id) this.fetchDiagnosticProtocolPlayerStats('player1', this.player1.id)
      if (this.player2.id) this.fetchDiagnosticProtocolPlayerStats('player2', this.player2.id)
    },
    dateRange: function () {
      if (this.player1.id) this.fetchDiagnosticProtocolPlayerStats('player1', this.player1.id)
      if (this.player2.id) this.fetchDiagnosticProtocolPlayerStats('player2', this.player2.id)
    },
    trendStats: function (val) {
      this.view = val.length <= 1 ? 'list' : 'visual'
    }
  },
  mounted() {
    this.fetchAllPlayers()
  },
  methods: {
    async fetchAllPlayers() {
      try {
        this.players.loading = true

        const result = await PlayerApiService.getSimpleList({ order_by: 'last_name', order: 'ASC', team_id: this.selectedTeam?.id })

        this.players.data = result.data
      } finally {
        this.players.loading = false
      }
    },
    async fetchDiagnosticStats(identificator, value) {
      if (identificator === 'player1') {
        this.loadingPlayer1 = true
      } else {
        this.loadingPlayer2 = true
      }

      try {
        const filters = {
          team_id: this.filters.teamId,
          date_from: this.filters.date.length > 0 ? this.filters.date[0] : null,
          date_to: this.filters.date.length > 0 ? this.filters.date[1] : null,
          age_from: this.filters.age[0],
          age_to: this.filters.age[1],
          rank_id: this.filters.rankId,
          sport_id: this.filters.sportId,
          country_code: this.filters.countryCode,
          position_id: this.filters.positionId
        }

        const result = await PlayerApiService.getDiagnosticStats(value.id, filters)

        if (identificator === 'player1') {
          this.player1 = {
            ...this.player1,
            diagnosticStats: result
          }
        } else {
          this.player2 = {
            ...this.player2,
            diagnosticStats: result
          }
        }
      } catch (e) {
        if (identificator === 'player1') {
          this.player1.diagnosticStats = null
        } else {
          this.player2.diagnosticStats = null
        }
        throw e
      } finally {
        if (identificator === 'player1') {
          this.loadingPlayer1 = false
        } else {
          this.loadingPlayer2 = false
        }
      }
    },
    async fetchDiagnosticProtocols(playerKey, playerId) {
      try {
        this.diagnosticProtocols[playerKey].loading = true

        const result = await DiagnosticProtocolApiService.getAll({
          order_by: 'name',
          player_id: playerId
        })

        this.diagnosticProtocols[playerKey].data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_protocols.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.diagnosticProtocols[playerKey].loading = false
      }
    },
    async fetchDiagnosticProtocolPlayerStats(playerKey, playerId) {
      if (!this.diagnosticProtocols.selected) return

      this.stats[playerKey].loading = true

      try {
        this.stats[playerKey].data = await PlayerApiService.getDiagnosticProtocolPlayerStats(playerId, this.diagnosticProtocols.selected.id, {
          date_from: dayjs().subtract(this.dateRange, 'month').format('YYYY-MM-DD'),
          date_to: dayjs().format('YYYY-MM-DD')
        })

        this.initVariables()
      } catch (e) {
        this.stats[playerKey].data = []
        throw e
      } finally {
        this.stats[playerKey].loading = false
      }
    },
    initVariables() {
      const player1Variables = this.stats.player1.data.map(stat => stat.variables).flat()
      const player2Variables = this.stats.player2.data.map(stat => stat.variables).flat()

      let variables = []
      if (player1Variables.length && player2Variables.length) {
        variables = player1Variables.filter(p1Var => player2Variables.some(p2Var => p2Var.var_id === p1Var.var_id))
      } else {
        variables = player1Variables.length ? player1Variables : player2Variables
      }

      this.variablesGrouped.data = uniqBy(variables, 'var_id')
      this.variablesGrouped.selected = this.variablesGrouped.data[0] || null
    },
    applyFilters() {
      if (this.player1.id) this.fetchDiagnosticStats('player1', this.player1)
      if (this.player2.id) this.fetchDiagnosticStats('player2', this.player2)
    },
    getPlayerName(player) {
      return player === 'player1' ? this.player1?.name || 'N/A' : this.player2?.name || 'N/A'
    },
    getValeForDate(date, identificator) {
      const value = this.trendStats.find(s => s.start_at === date)[identificator].value
      return value || 0
    }
  }
}
</script>
